/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import sectionStyles, { themesectionStyles } from './section.style';

const CHILDREN_PLACEHOLDER_ID = 'children';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    id?: string;
    className?: string;
    typographyClass?: string;
    isTransformData?: boolean;
    functionToTransform?: string;
    sectionType?: string;
}

const Section = (props: commonTypes.WidgetInstanceProps) => {
    const { Placeholder } = commonHooks.useAppContext();
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, id, className, isTransformData, functionToTransform, sectionType } =
        widgetProperties || {};

    const { data } = props;
    const theme = data?.themeSelected || (props as commonTypes.WidgetInstanceProps).theme;

    let isRender = {
        renderSection: true,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const transformedData =
            isTransformData &&
            commonUtils.transformDataWithGlobals(transformFunctionName, functionToTransform, data);

        if (transformedData) {
            isRender = { ...isRender, ...transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    return isRender.renderSection ? (
        sectionType === 'aside' ? (
            <aside
                id={id}
                className={`section ${className || ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? sectionStyles : themesectionStyles}
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
                data-testid="section"
            >
                <Placeholder
                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                    parentWidgetInstanceId={widgetInstance.id}
                    data={data}
                />
            </aside>
        ) : sectionType === 'main' ? (
            <main
                id={id}
                className={`section ${className || ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? sectionStyles : themesectionStyles}
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
                data-testid="section"
            >
                <Placeholder
                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                    parentWidgetInstanceId={widgetInstance.id}
                    data={data}
                />
            </main>
        ) : sectionType === 'nav' ? (
            <nav
                id={id}
                className={`section ${className || ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? sectionStyles : themesectionStyles}
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
                data-testid="section"
            >
                <Placeholder
                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                    parentWidgetInstanceId={widgetInstance.id}
                    data={data}
                />
            </nav>
        ) : sectionType === 'header' ? (
            <header
                id={id}
                className={`section ${className || ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? sectionStyles : themesectionStyles}
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
                data-testid="section"
            >
                <Placeholder
                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                    parentWidgetInstanceId={widgetInstance.id}
                    data={data}
                />
            </header>
        ) : sectionType === 'footer' ? (
            <footer
                id={id}
                className={`section ${className || ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? sectionStyles : themesectionStyles}
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
                data-testid="section"
            >
                <Placeholder
                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                    parentWidgetInstanceId={widgetInstance.id}
                    data={data}
                />
            </footer>
        ) : (
            <div
                id={id}
                className={`section ${className || ''} ${
                    widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                }`}
                css={css`
                    ${!theme ? sectionStyles : themesectionStyles}
                    ${getTextSchemaProperties(
                        widgetProperties,
                        data?.colorSpace
                    )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                        widgetProperties
                    )}${styles}
                `}
                data-testid="section"
            >
                <Placeholder
                    contentPlaceHolderId={CHILDREN_PLACEHOLDER_ID}
                    parentWidgetInstanceId={widgetInstance.id}
                    data={data}
                />
            </div>
        )
    ) : null;
};

export default Section;
